import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { BaseComponent } from '../classes/BaseComponent';
import { FileUploadListener } from '../classes/FileUploadListener';
import { StringHelper } from '../helpers/StringHelper';
import { StorageHelper } from '../helpers/StorageHelper';
import { Global } from '../classes/Global';
import { WishlistService } from '../wishlist.service';

export interface StoreImportDetails {
  status: string;
  added: number;
  notAdded: number;
  alreadyAdded: number;
  notAddedTitlesCsv: string;
}

export interface ImportDetails {
  [storeName: string]: StoreImportDetails;
}

@Component({
  selector: 'app-import-letterboxd-list',
  templateUrl: './import-letterboxd-list.component.html',
  styleUrls: ['./import-letterboxd-list.component.scss'],
  providers: [WishlistService]
})
export class ImportLetterboxdListComponent extends BaseComponent implements OnInit, FileUploadListener {

  MAX_FILE_SIZE_BYTES = 128000000;

  @ViewChild('browseInput', { static: false }) browseInput;
  @Input() pages: Page[] = new Array(4);
  currentPageNo = 0;
  @Output() onClose = new EventEmitter();
  @Output() onNext = new EventEmitter();

  isUploading = false;
  uploadSuccessful = true;
  successCount = 0;
  uploadErrorMessage = "";
  importResultDetails: ImportDetails | null = null;

  iTunesOn = true;
  amazonOn = true;
  vuduOn = true;
  fileOK = false;
  invalidFile = false;
  fileTooBig = false;

  errorMessage = "";
  file;
  generatedZipFile;

  constructor(
    private wishlistService: WishlistService
  ) {
    super();
  }

  ngOnInit() {
  }

  next() {

    if (!this.iTunesOn && !this.amazonOn && !this.vuduOn) {
      this.errorMessage = this.getString("importimdbPage3Checkbox");
      return;
    }

    if (this.currentPageNo === 2) {

      if (this.file) {
        this.isUploading = true;
        this.zipFile(this.file);
        return;
      }
      else if (this.generatedZipFile && this.isUploading) {
        this.isUploading = false;
      }
      else if (!this.generatedZipFile) {
        this.invalidFile = true;
        return;
      }

    }

    if (this.currentPageNo < this.pages.length - 1) {
      this.currentPageNo++;
    }

    this.onNext.emit(this.currentPageNo);
    const content = document.getElementsByClassName("content-ii")[0];
    if (!content) { return; }

    let w = content.clientWidth;
    const interval = setInterval(() => {
      if (w > 0) {
        const pixelsToScroll = w > 40 ? 40 : w;
        w -= pixelsToScroll;
        const element = document.getElementsByClassName("slide-container-ii")[0];
        if (element && element.scrollBy) {
          element.scrollBy(pixelsToScroll, 0);
        }

      }
      else {
        clearInterval(interval);
      }
    }, 25);

  }

  close() {
    this.onClose.emit();
  }

  cbiTunesChanged(value) {
    this.iTunesOn = value;
    this.errorMessage = "";
  }

  cbAmazonChanged(value) {
    this.amazonOn = value;
    this.errorMessage = "";
  }

  cbVuduChanged(value) {
    this.vuduOn = value;
    this.errorMessage = "";
  }

  chooseFile() {
    this.browseInput.nativeElement.click();
    return false;
  }

  zipFile(file) {

    const stores = [];
    if (this.iTunesOn) { stores.push(Global.ITUNES); }
    if (this.amazonOn) { stores.push(Global.AMAZON); }
    if (this.vuduOn) { stores.push(Global.VUDU); }
    this.generatedZipFile = file;
    if (file.size > this.MAX_FILE_SIZE_BYTES) {
      this.fileOK = false;
      this.fileTooBig = true;
      this.isUploading = false;
      return;
    }
    this.wishlistService.uploadLetterboxdFile(this.generatedZipFile, this, Global.getCountry(),
      stores.join(','), StorageHelper.getInstance().get(StorageHelper.keys.sessionToken)).subscribe(res => {
        this.file = null;
        this.isUploading = false;
        try {
          res = JSON.parse(res);
        }
        catch (e) {
          this.uploadSuccessful = false;
          this.uploadErrorMessage = this.getString("error");
          this.importResultDetails = null;
          this.next();
          return;
        }
        if (res) {
          if (res.status === "error") {
            this.uploadSuccessful = false;
            this.uploadErrorMessage = res.message;
            this.importResultDetails = null;
          }
          else if (res.additionalInfo) {
            this.uploadSuccessful = true;
            const processedAdditionalInfo: ImportDetails = {};
            for (const storeKey in res.additionalInfo) {
              if (Object.prototype.hasOwnProperty.call(res.additionalInfo, storeKey)) {
                const storeData = res.additionalInfo[storeKey];
                processedAdditionalInfo[storeKey] = {
                  status: storeData.status || 'unknown',
                  added: (storeData.added !== null && storeData.added !== undefined) ? storeData.added : 0,
                  notAdded: (storeData.notAdded !== null && storeData.notAdded !== undefined) ? storeData.notAdded : 0,
                  alreadyAdded: (storeData.alreadyAdded !== null && storeData.alreadyAdded !== undefined) ? storeData.alreadyAdded : 0,
                  notAddedTitlesCsv: storeData.notAddedTitlesCsv || ''
                };
              }
            }
            this.importResultDetails = processedAdditionalInfo;
          } else {
            this.uploadSuccessful = false;
            this.uploadErrorMessage = this.getString("error");
            this.importResultDetails = null;
          }
        } else {
          this.uploadSuccessful = false;
          this.uploadErrorMessage = this.getString("error");
          this.importResultDetails = null;
        }
        this.next();
      }, err => {
        this.file = null;
        this.isUploading = false;
        this.uploadSuccessful = false;
        this.uploadErrorMessage = this.getString("error_occurred_during_upload");
        this.importResultDetails = null;
        this.next();
      });

  }

  handleSelectedLocalFiles(e) {

    this.fileOK = false;
    this.invalidFile = false;

    const files: FileList = (e.srcElement || e.target).files;

    const filesArray = [];
    for (let i = 0; i < files.length; i++) {
      filesArray.push(files.item(i));
    }
    if (filesArray.length > 0) {

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const fileContent = fileReader.result;
        const firstLine = fileContent.toString().split('\n')[0].trim();

        // Check if the first line matches the expected Letterboxd CSV header
        if (firstLine === "Date,Name,Year,Letterboxd URI") {
          this.fileOK = true;
        }
        else {
          this.invalidFile = true;
        }
      };
      fileReader.readAsText(filesArray[0]);
      this.file = filesArray[0];
    }
    this.browseInput.nativeElement.value = "";
  }

  onProgress(file: any, progressPercentage: number) { }

  getErrorMessageText(): string {
    return StringHelper.format(this.getString('importPage4ErrorText2'), "" + this.uploadErrorMessage);
  }

  getStoreKeys(details: ImportDetails | null): string[] {
    return details ? Object.keys(details) : [];
  }

}

export class Page {
  text: string;
  img: string;
}
